import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Col, Row, message } from 'antd';
import {
	getRAGTiers,
	getRAGTeam,
	getRAGTeamBudgetChanges,
	getRAGTeamRenewalPeriodChanges,
	putRAGTeamBudget,
	putRAGTeamRenewalPeriod,
	postRAGTeam,
	toggleRAGTeamActiveStatus,
	toggleRAGUserActiveStatus,
	getRAGTeamUsers,
} from '../../utils/api';
import { RENEWAL_PERIOD } from '../../config/common';
import SocratesChangeBudgetModal from './socratesChangeBudgetModal';
import SocratesChangeRenewalPeriodModal from './socratesChangeRenewalPeriodModal';
import SocratesAddTeamModal from './socratesAddTeamModal';

const SocratesManagement = ({ organizationId, enabled }) => {
	const [ragTiers, setRagTiers] = useState([]);
	const [ragTeam, setRagTeam] = useState(null);
	const [ragTeamBudgetChanges, setRagTeamBudgetChanges] = useState([]);
	const [ragTeamRenewalPeriodChanges, setRagTeamRenewalPeriodChanges] = useState([]);
	const [openChangeBudgetModal, setOpenChangeBudgetModal] = useState(false);
	const [openChangeRenewalPeriodModal, setOpenChangeRenewalPeriodModal] = useState(false);
	const [openAddTeamModal, setOpenAddTeamModal] = useState(false);

	useEffect(() => {
		if (enabled == null) {
			return;
		}

		const handleRagTeam = async (organizationId) => {
			try {
				await getRAGTeam(organizationId).then(async (team) => {
					if (enabled) {
						if (team.isActive) {
							setRagTeam(team);
						} else {
							await toggleRAGTeamActiveStatus(team.id).then((team) => {
								setRagTeam(team);
							});
						}
					} else {
						if (team.isActive) {
							await toggleRAGTeamActiveStatus(team.id).then((team) => {
								setRagTeam(team);

								// Disable all RAG users for this team
								getRAGTeamUsers(team.id).then((users) => {
									users.forEach((user) => {
										try {
											toggleRAGUserActiveStatus(user.id).then((ragUser) => {
												// RAG user disabled, fall through
											});
										} catch (error) {
											// RAG user missing, fall through
										}
									});
								});
							});
						}
					}
				});
			} catch (error) {
				// No RAG team found, but Socrates is enabled, prompt user to add team manually
				if (enabled) {
					setOpenAddTeamModal(true);
				}
			}
		};

		if (organizationId) {
			handleRagTeam(organizationId);
		}
	}, [enabled, organizationId]);

	const handleChangeBudgetSubmit = async (tierID) => {
		putRAGTeamBudget(ragTeam.id, tierID).then((team) => {
			setRagTeam(team);
			setOpenChangeBudgetModal(false);
		});
	};

	const changeBudgetModal = () => {
		setOpenChangeBudgetModal(true);
	};

	const handleChangeRenewalPeriodSubmit = async (renewalPeriod) => {
		putRAGTeamRenewalPeriod(ragTeam.id, renewalPeriod).then((team) => {
			setRagTeam(team);
			setOpenChangeRenewalPeriodModal(false);
		});
	};

	const changeRenewalPeriodModal = async () => {
		setOpenChangeRenewalPeriodModal(true);
	};

	const handleAddTeamSubmit = async (formData) => {
		try {
			await postRAGTeam(
				organizationId,
				formData.tierID,
				formData.canShareQuota,
				formData.renewalPeriod,
				formData.isActive
			).then((team) => {
				setRagTeam(team);
				setOpenAddTeamModal(false);
				message.success('Kund har lagts till i Socrates', 7);
			});
		} catch (error) {
			console.error('Error adding team:', error);
			message.error('Kunde inte lägga till kund i Socrates', 7);
		}
	};

	// Fetch RAG tiers data
	useEffect(() => {
		const fetchRagTiers = async () => {
			await getRAGTiers().then((tiers) => {
				setRagTiers(tiers);
			});
		};
		fetchRagTiers();
	}, []);

	// Fetch RAG team budget changes data
	useEffect(() => {
		const fetchRagTeamBudgetChanges = async (teamId) => {
			await getRAGTeamBudgetChanges(teamId).then((budgetChanges) => {
				setRagTeamBudgetChanges(budgetChanges);
			});
		};
		if (ragTeam && ragTeam.id) {
			fetchRagTeamBudgetChanges(ragTeam.id);
		}
	}, [ragTeam]);

	// Fetch RAG team renewal period changes data
	useEffect(() => {
		const fetchRagTeamRenewalPeriodChanges = async (teamId) => {
			await getRAGTeamRenewalPeriodChanges(teamId).then((renewalPeriodChanges) => {
				setRagTeamRenewalPeriodChanges(renewalPeriodChanges);
			});
		};
		if (ragTeam && ragTeam.id) {
			fetchRagTeamRenewalPeriodChanges(ragTeam.id);
		}
	}, [ragTeam]);

	const printTierQuota = (tierID) => {
		const tier = ragTiers.find((tier) => tier.id === tierID);
		return tier.quota;
	};

	if (!enabled) {
		return null;
	}

	return ragTeam && ragTiers.length > 0 ? (
		<div>
			<Row style={{ padding: '18px 0' }}>
				<Col span={12}>
					<h2>Socrates</h2>
				</Col>
				<Col span={12}>
					<Button.Group style={{ float: 'right' }}>
						<Button type='primary' icon='dollar' size='default' onClick={changeBudgetModal}>
							Ändra budget
						</Button>
						<Button
							type='secondary'
							icon='clock-circle-o'
							size='default'
							onClick={changeRenewalPeriodModal}
						>
							Ändra fakturerings-intervall
						</Button>
					</Button.Group>
				</Col>
			</Row>
			<Card>
				<Row>
					<Col span={12}>
						<Row style={{ marginBottom: 16 }}>
							<h3>Budget</h3>
							<div>{`${printTierQuota(ragTeam.tierID)} frågor`}</div>
						</Row>
						{ragTeamBudgetChanges.length > 0 && (
							<Row>
								<h4>Planerad uppdatering av budget</h4>
								<div>
									<span>{`${printTierQuota(
										ragTeamBudgetChanges[ragTeamBudgetChanges.length - 1].newTierID
									)} frågor`}</span>
									<span style={{ marginLeft: 8, color: 'orange' }}>
										(
										{new Date(
											ragTeamBudgetChanges[ragTeamBudgetChanges.length - 1].executionAt
										).toLocaleDateString('sv-SE')}
										)
									</span>
								</div>
							</Row>
						)}
					</Col>
					<Col span={12}>
						<Row style={{ marginBottom: 16 }}>
							<h3>Fakturerings-intervall</h3>
							<div>{RENEWAL_PERIOD[ragTeam.renewalPeriod].label}</div>
						</Row>
						{ragTeamRenewalPeriodChanges.length > 0 && (
							<Row>
								<h4>Planerad uppdatering av fakturerings-intervall</h4>
								<div>
									<span>
										{
											RENEWAL_PERIOD[
												ragTeamRenewalPeriodChanges[ragTeamRenewalPeriodChanges.length - 1]
													.newRenewalPeriod
											].label
										}
									</span>
									<span style={{ marginLeft: 8, color: 'orange' }}>
										(
										{new Date(
											ragTeamRenewalPeriodChanges[
												ragTeamRenewalPeriodChanges.length - 1
											].executionAt
										).toLocaleDateString('sv-SE')}
										)
									</span>
								</div>
							</Row>
						)}
					</Col>
				</Row>
			</Card>
			<SocratesChangeBudgetModal
				open={openChangeBudgetModal}
				team={ragTeam}
				tiers={ragTiers}
				onCancel={() => {
					setOpenChangeBudgetModal(false);
				}}
				onSubmit={(formData) => {
					handleChangeBudgetSubmit(formData.tiers);
				}}
			/>
			<SocratesChangeRenewalPeriodModal
				open={openChangeRenewalPeriodModal}
				team={ragTeam}
				onCancel={() => {
					setOpenChangeRenewalPeriodModal(false);
				}}
				onSubmit={(formData) => {
					handleChangeRenewalPeriodSubmit(formData.renewalPeriod);
				}}
			/>
		</div>
	) : (
		<div>
			<Row>
				<Col span={12}>
					<h2>Socrates</h2>
				</Col>
				<Col span={12}>
					<Button.Group style={{ float: 'right' }}>
						<Button type='primary' icon='plus' onClick={() => setOpenAddTeamModal(true)}>
							Lägg till kund i Socrates
						</Button>
					</Button.Group>
				</Col>
			</Row>
			<Row>
				<div>Socrates plustjänst är aktiv men kunden är inte tillagd i Socrates ännu.</div>
			</Row>
			{ragTiers.length > 0 && (
				<SocratesAddTeamModal
					open={openAddTeamModal}
					organizationId={organizationId.toString()}
					tiers={ragTiers}
					onCancel={() => {
						setOpenAddTeamModal(false);
					}}
					onSubmit={handleAddTeamSubmit}
				/>
			)}
		</div>
	);
};

SocratesManagement.propTypes = {
	organizationId: PropTypes.number.isRequired,
	onChange: PropTypes.func,
	enabled: PropTypes.bool.isRequired,
};

export default SocratesManagement;



// WEBPACK FOOTER //
// ./src/components/socrates/socratesManagement.jsx